import { Box } from '@material-ui/core';
import Close from '@material-ui/icons/Close';
import { ReactElement, useEffect, useState } from 'react';
import {
	Control,
	Path,
	UseFormGetValues,
	UseFormSetValue
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TabContainer } from '..';
import { AttachmentType } from '../../../models';
import { getCloudinaryContent, isCloudinaryAsset } from '../../../utils';
import { Body1, UploadFileField, VideoPlayer } from '../../atoms';
import { ImageInputField } from '../image-input-field/image-input-field.component';
import { VideoInputField } from '../video-input-field/video-input-field.component';

import './media-input-field.styles.scss';

export interface IMediaInputFieldProps<T> {
	remoteUrl?: string;
	control: Control<T>;
	name: Path<T>;
	label?: string;
	placeholder?: string;
	required?: boolean;
	fullWidth?: boolean;
	valid?: boolean;
	blob?: string;
	maxFileSizeInMb?: number;
	type?: AttachmentType;
	onSelect?: (file: File | null) => void;
	onValidChanged?: (isValid: boolean) => void;
	setValue: UseFormSetValue<T>;
	getValues: UseFormGetValues<T>;
}

export function MediaInputField<T>(
	props: IMediaInputFieldProps<T>
): ReactElement {
	const {
		remoteUrl,
		label,
		name,
		blob,
		maxFileSizeInMb,
		type,
		onSelect,
		onValidChanged,
		setValue,
		getValues
	} = props;

	const { t } = useTranslation('map');

	const maxFileSize: number | undefined = maxFileSizeInMb
		? maxFileSizeInMb * 1024 * 1024
		: undefined;

	const [file, setFile] = useState<File | null>(null);
	const [fileUrl, setFileUrl] = useState<any>(getValues(name));
	const [activeTab, setActiveTab] = useState<number>(
		!isCloudinaryAsset(remoteUrl) && remoteUrl ? 1 : 0
	);
	const [validFileSize, setValidFileSize] = useState<boolean>(true);

	useEffect(() => {
		if (file && maxFileSize && file.size > maxFileSize) setValidity(false);
		else setValidity(true);
	}, [file]);

	function handleTabChange(tab: number): void {
		if (activeTab === 0 && isCloudinaryAsset(remoteUrl)) {
			setFileUrl(getValues(name));
			setValue(name, '' as any);
		} else {
			setValue(name, fileUrl);
		}
		setActiveTab(tab);
	}

	function setValidity(isValid: boolean): void {
		setValidFileSize(isValid);
		if (onValidChanged) onValidChanged(isValid);
	}

	function getAllowedTypes(): string[] {
		switch (type) {
			case AttachmentType.Image:
				return ['image/png', 'image/jpg', 'image/jpeg'];
			case AttachmentType.Video:
				return ['video/mp4'];
			default:
				return [];
		}
	}

	function getAcceptTypes(): string {
		switch (type) {
			case AttachmentType.Image:
				return '.png, .jpg';
			case AttachmentType.Video:
				return '.mp4';
			default:
				return '';
		}
	}

	function getTypeString(): string {
		switch (type) {
			case AttachmentType.Image:
				return t('attachments.image.imageTitle');
			case AttachmentType.Video:
				return t('attachments.video.videoTitle');
			default:
				return '';
		}
	}

	function handleUpload(file: File | null) {
		if (file) {
			if (getAllowedTypes().includes(file.type)) {
				setFile(file);
				if (onSelect) onSelect(file);
			}
		} else {
			setFile(null);
			if (onSelect) onSelect(null);
		}
	}

	function UploadTab(): ReactElement {
		return (
			<>
				<Box className="upload-field">
					<UploadFileField
						file={file}
						buttonOnly
						accept={getAcceptTypes()}
						onSelect={handleUpload}
					/>
					<Body1
						className={`media-help ${validFileSize ? '' : 'error'}`}
					>
						{validFileSize
							? t('attachments.maximumMB')
							: t('attachments.maximumExceeded')}
					</Body1>
				</Box>
				{blob && blob.length > 0 && (
					<Box className="upload-preview">
						{file && (
							<Box
								display="grid"
								gridTemplateColumns="10fr 2fr 1fr"
							>
								<Body1>{file?.name}</Body1>
								<Body1>
									({(file.size / 1024 / 1024).toFixed(2)}
									MB)
								</Body1>
								<Box onClick={() => handleUpload(null)}>
									<Close />
								</Box>
							</Box>
						)}
						{type && type === AttachmentType.Image && (
							<img src={blob} alt={label} />
						)}
						{type && type === AttachmentType.Video && (
							<VideoPlayer videoUrl={blob} />
						)}
					</Box>
				)}
				{isCloudinaryAsset(remoteUrl) && type === AttachmentType.Image && (
					<Box className="upload-preview">
						<img
							src={getCloudinaryContent(
								remoteUrl as string,
								'image'
							)}
							alt={label}
						/>
					</Box>
				)}
				{isCloudinaryAsset(remoteUrl) && type === AttachmentType.Video && (
					<Box className="upload-preview">
						<VideoPlayer videoUrl={remoteUrl as string} />
					</Box>
				)}
			</>
		);
	}

	function UrlTab(): ReactElement {
		return (
			<>
				{type && type === AttachmentType.Image && (
					<ImageInputField
						{...props}
						imageUrl={remoteUrl}
						hideLabel
					/>
				)}
				{type && type === AttachmentType.Video && (
					<VideoInputField
						{...props}
						videoUrl={remoteUrl}
						hideLabel
					/>
				)}
			</>
		);
	}

	return (
		<Box className="media-input-field">
			{onSelect && (
				<TabContainer
					tabs={[
						t('attachments.upload', { type: getTypeString() }),
						t('attachments.url', { type: getTypeString() })
					]}
					activeTab={activeTab}
					requiredLabel
					onTabChange={handleTabChange}
				/>
			)}
			{activeTab === 0 && <UploadTab />}
			{activeTab === 1 && <UrlTab />}
		</Box>
	);
}
